import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Flame, DollarSign, ArrowRight, ArrowLeft, CheckCircle2, Home, Briefcase, Phone, User, Shield, Clock, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES_LIST = [
  { id: "electricity", label: "Residential Electricity", icon: Zap, desc: "Find cheaper electricity rates for your home" },
  { id: "business-energy", label: "Business Electricity", icon: DollarSign, desc: "Electricity comparison for your business" },
];

const PROVIDERS = [
  "AGL", "Origin Energy", "Energy Australia", "Alinta Energy", "Red Energy",
  "Simply Energy", "Momentum Energy", "Lumo Energy", "Dodo Power & Gas",
  "Powershop", "Tango Energy", "1st Energy", "Blue NRG", "ActewAGL", "Other", "Not Sure"
];

const slideVariants = {
  enter: { x: 30, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -30, opacity: 0 },
};

function StepServiceType({ data, setData, next }) {
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-[#2E2E33] mb-2">What would you like to compare?</h2>
      <p className="text-gray-500 mb-8">Select the energy service you're looking to save on.</p>
      <div className="space-y-3">
        {SERVICES_LIST.map((s) => (
          <button
            key={s.id}
            onClick={() => { setData({ ...data, service_type: s.id }); next(); }}
            className={`w-full flex items-center gap-5 p-5 rounded-2xl border-2 transition-all text-left ${
              data.service_type === s.id
                ? "border-[#FF6600] bg-[#FF6600]/5"
                : "border-[#E2E8F0] hover:border-[#FF6600]/40 hover:bg-[#F8FAFC]"
            }`}
            data-testid={`service-option-${s.id}`}
          >
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${data.service_type === s.id ? "bg-[#FF6600]" : "bg-[#F8FAFC]"}`}>
              <s.icon className={`w-7 h-7 ${data.service_type === s.id ? "text-white" : "text-[#FF6600]"}`} strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-bold text-[#2E2E33] text-lg">{s.label}</p>
              <p className="text-sm text-gray-500">{s.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepCustomerType({ data, setData, next, prev }) {
  const options = [
    { id: "residential", label: "Home / Residential", icon: Home, desc: "Comparing for my household" },
    { id: "business", label: "Business / Commercial", icon: Briefcase, desc: "Comparing for my business" },
  ];
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-[#2E2E33] mb-2">Is this for your home or business?</h2>
      <p className="text-gray-500 mb-8">This helps us find the most relevant plans for you.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => { setData({ ...data, customer_type: o.id }); next(); }}
            className={`flex flex-col items-center gap-4 p-10 rounded-3xl border-2 transition-all ${
              data.customer_type === o.id ? "border-[#FF6600] bg-[#FF6600]/5" : "border-[#E2E8F0] hover:border-[#FF6600]/40"
            }`}
            data-testid={`customer-type-${o.id}`}
          >
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center ${data.customer_type === o.id ? "bg-[#FF6600]" : "bg-[#F8FAFC]"}`}>
              <o.icon className={`w-10 h-10 ${data.customer_type === o.id ? "text-white" : "text-[#FF6600]"}`} strokeWidth={1.5} />
            </div>
            <p className="font-extrabold text-[#2E2E33] text-lg">{o.label}</p>
            <p className="text-sm text-gray-500">{o.desc}</p>
          </button>
        ))}
      </div>
      <button onClick={prev} className="flex items-center gap-2 mt-8 text-sm font-medium text-gray-500 hover:text-[#2E2E33]" data-testid="wizard-prev-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
    </div>
  );
}

function StepDetails({ data, setData, next, prev }) {
  const update = (key, val) => setData({ ...data, [key]: val });
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-[#2E2E33] mb-2">Tell us about your current setup</h2>
      <p className="text-gray-500 mb-8">This helps us find the best deals for your situation.</p>
      <div className="space-y-5">
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Your Address</label>
          <Input value={data.address || data.postcode} onChange={(e) => update("postcode", e.target.value)} placeholder="Enter your address or postcode" className="h-14 rounded-xl text-base" data-testid="wizard-postcode" />
        </div>
        {data.customer_type === "business" && (
          <div>
            <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Business Name</label>
            <Input value={data.business_name} onChange={(e) => update("business_name", e.target.value)} placeholder="Your business name" className="h-14 rounded-xl text-base" data-testid="wizard-business-name" />
          </div>
        )}
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Current Provider</label>
          <Select onValueChange={(val) => update("current_provider", val)} value={data.current_provider}>
            <SelectTrigger className="h-14 rounded-xl text-base" data-testid="wizard-provider-select">
              <SelectValue placeholder="Select your current provider" />
            </SelectTrigger>
            <SelectContent>
              {PROVIDERS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Approximate Quarterly Bill ($)</label>
          <Input type="number" value={data.quarterly_bill} onChange={(e) => update("quarterly_bill", e.target.value)} placeholder="e.g. 450" className="h-14 rounded-xl text-base" data-testid="wizard-bill" />
        </div>
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Do you have solar panels?</label>
          <div className="flex gap-4">
            {["Yes", "No"].map((opt) => (
              <button
                key={opt}
                onClick={() => update("has_solar", opt === "Yes")}
                className={`flex-1 py-4 rounded-xl border-2 font-bold text-base transition-all ${
                  (data.has_solar && opt === "Yes") || (!data.has_solar && opt === "No")
                    ? "border-[#FF6600] bg-[#FF6600]/5 text-[#FF6600]"
                    : "border-[#E2E8F0] text-gray-500 hover:border-[#FF6600]/40"
                }`}
                data-testid={`wizard-solar-${opt.toLowerCase()}`}
              >{opt}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-between mt-10">
        <button onClick={prev} className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#2E2E33]" data-testid="wizard-prev-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Button onClick={next} className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold px-8 py-4 rounded-xl text-base" data-testid="wizard-next-btn">
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function StepContact({ data, setData, submit, prev, loading }) {
  const update = (key, val) => setData({ ...data, [key]: val });
  const canSubmit = data.full_name && data.email && data.phone;
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-[#2E2E33] mb-2">Your Contact Details</h2>
      <p className="text-gray-500 mb-8">We'll send you personalised comparison results.</p>
      <div className="space-y-5">
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Full Name *</label>
          <Input value={data.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="John Smith" className="h-14 rounded-xl text-base" data-testid="wizard-name" />
        </div>
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Email *</label>
          <Input type="email" value={data.email} onChange={(e) => update("email", e.target.value)} placeholder="john@example.com" className="h-14 rounded-xl text-base" data-testid="wizard-email" />
        </div>
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Phone *</label>
          <Input type="tel" value={data.phone} onChange={(e) => update("phone", e.target.value)} placeholder="04XX XXX XXX" className="h-14 rounded-xl text-base" data-testid="wizard-phone" />
        </div>
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Best Time to Contact</label>
          <Select onValueChange={(val) => update("preferred_contact_time", val)} value={data.preferred_contact_time}>
            <SelectTrigger className="h-14 rounded-xl text-base" data-testid="wizard-contact-time">
              <SelectValue placeholder="Select preferred time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
              <SelectItem value="afternoon">Afternoon (12pm - 3pm)</SelectItem>
              <SelectItem value="evening">Evening (3pm - 6pm)</SelectItem>
              <SelectItem value="anytime">Any Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[15px] font-bold text-[#2E2E33] mb-2">Additional Notes</label>
          <textarea
            value={data.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Any specific requirements?"
            className="w-full h-28 rounded-xl border-2 border-[#E2E8F0] p-4 text-base focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] outline-none resize-none"
            data-testid="wizard-notes"
          />
        </div>
      </div>
      <div className="flex justify-between mt-10">
        <button onClick={prev} className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#2E2E33]" data-testid="wizard-prev-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Button onClick={submit} disabled={!canSubmit || loading} className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold px-8 py-4 rounded-xl text-base disabled:opacity-40" data-testid="wizard-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Get My Free Quote <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
      <p className="text-xs text-gray-400 mt-6 text-center">
        By submitting, you agree to be contacted by Sales Corp Australia. Your information is kept secure and private.
      </p>
    </div>
  );
}

function StepConfirmation() {
  return (
    <div className="text-center py-10">
      <div className="w-24 h-24 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-8">
        <CheckCircle2 className="w-12 h-12 text-[#10B981]" />
      </div>
      <h2 className="text-3xl md:text-4xl font-extrabold text-[#2E2E33] mb-4">Thank You!</h2>
      <p className="text-lg text-gray-500 mb-2">Your comparison request has been submitted.</p>
      <p className="text-gray-500 mb-10">One of our specialists will be in touch within 24 hours with your personalised results.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/">
          <Button variant="outline" className="border-2 border-[#2E2E33] text-[#2E2E33] font-bold rounded-full px-8 py-3" data-testid="wizard-back-home">
            Back to Home
          </Button>
        </Link>
        <a href="tel:1300796797">
          <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-3" data-testid="wizard-call-now">
            <Phone className="w-4 h-4 mr-2" /> Call Us Now
          </Button>
        </a>
      </div>
    </div>
  );
}

const STEP_LABELS = ["Service", "Type", "Details", "Contact"];

export default function ComparePage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [data, setData] = useState({
    service_type: searchParams.get("service") || "",
    customer_type: "",
    postcode: searchParams.get("address") || searchParams.get("postcode") || "",
    current_provider: "",
    quarterly_bill: "",
    has_solar: false,
    business_name: "",
    full_name: "",
    email: "",
    phone: "",
    preferred_contact_time: "",
    notes: "",
  });

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, data);
      setSubmitted(true);
      toast.success("Quote request submitted successfully!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const progress = submitted ? 100 : ((step + 1) / STEP_LABELS.length) * 100;

  return (
    <div className="py-12 md:py-20" data-testid="compare-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border-2 border-[#E2E8F0] rounded-3xl shadow-2xl p-6 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-[#E2E8F0]">
            <div className="h-full bg-[#10B981] transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
          </div>

          {!submitted && (
            <div className="flex items-center justify-center gap-3 mb-10 mt-5">
              {STEP_LABELS.map((label, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    i <= step ? "bg-[#FF6600] text-white" : "bg-[#E2E8F0] text-gray-400"
                  }`}>
                    {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`hidden sm:block text-sm font-medium ${i <= step ? "text-[#2E2E33]" : "text-gray-400"}`}>{label}</span>
                  {i < STEP_LABELS.length - 1 && <div className={`w-8 h-0.5 rounded ${i < step ? "bg-[#FF6600]" : "bg-[#E2E8F0]"}`} />}
                </div>
              ))}
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={submitted ? "done" : step}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
            >
              {submitted ? (
                <StepConfirmation />
              ) : step === 0 ? (
                <StepServiceType data={data} setData={setData} next={next} />
              ) : step === 1 ? (
                <StepCustomerType data={data} setData={setData} next={next} prev={prev} />
              ) : step === 2 ? (
                <StepDetails data={data} setData={setData} next={next} prev={prev} />
              ) : (
                <StepContact data={data} setData={setData} submit={submit} prev={prev} loading={loading} />
              )}
            </motion.div>
          </AnimatePresence>

          {!submitted && (
            <div className="mt-10 pt-6 border-t border-[#E2E8F0] flex items-center justify-center gap-8 text-sm text-gray-400 font-medium">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> 100% Free</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#FF6600]" /> Under 2 mins</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#2E2E33]" /> No Obligation</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
